import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Menu, Mail } from 'lucide-react'; // Added Mail icon

// Import Shadcn UI components
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose // Added SheetClose
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils"; // Import cn utility

export function Navbar() {
  // Keep state for mobile menu
  const [isSheetOpen, setIsSheetOpen] = useState(false); 

  // Keep existing logo click handler
  const handleLogoClick = () => {
    // Simple reload might not be ideal UX, consider navigate('/') if on landing page
    window.location.href = '/'; // Navigate to root instead of reload
    // window.location.reload(); 
  };

  // Simplified smooth scroll handler for hash links
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    if (targetId.startsWith('#')) {
      e.preventDefault();
      const element = document.getElementById(targetId.substring(1));
      if (element) {
          // Optional: Add offset if navbar height interferes
          // const yOffset = -80; // Adjust as needed
          // const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          // window.scrollTo({top: y, behavior: 'smooth'});
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Close sheet if clicking a hash link
      setIsSheetOpen(false); 
    } 
    // For non-hash links like /login or /signup, let the default Link behavior handle it.
  };

  return (
    // Use Shadcn theme variables and simplify classes
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-colors duration-300">
      <div className="container flex h-14 max-w-screen-2xl items-center">
        {/* Logo */}
        <Link to="/" onClick={handleLogoClick} className="mr-6 flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm">
          <img
            src="/images/logo.png" 
            alt="TopMatchTalent Logo"
            className="h-8 w-auto" // Keep size reasonable
          />
          {/* Site Name - hidden on small screens? */}
          {/* <span className="hidden sm:inline-block font-bold text-foreground">TopMatchTalent</span> */}
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden md:flex flex-1">
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink 
                href="#features" 
                onClick={(e) => handleNavClick(e, '#features')}
                className={navigationMenuTriggerStyle()}
              >
                Features
              </NavigationMenuLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuLink 
                href="#pricing" 
                onClick={(e) => handleNavClick(e, '#pricing')}
                className={navigationMenuTriggerStyle()}
              >
                Pricing
              </NavigationMenuLink>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuLink 
                href="#how-it-works" 
                onClick={(e) => handleNavClick(e, '#how-it-works')}
                className={navigationMenuTriggerStyle()}
              >
                How it Works
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Desktop Actions (Contact / Sign In / Sign Up) */}
        <div className="hidden md:flex items-center space-x-2 ml-auto"> {/* Use ml-auto to push right */}
          {/* Fix: Don't use asChild with an anchor inside */}
          <a 
            href="#contact" 
            onClick={(e) => handleNavClick(e, '#contact')}
            className="inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground px-3 h-9"
          >
            <Mail className="h-4 w-4" />
            Contact Us
          </a>
          
          <Button variant="ghost" asChild>
            <Link to="/login">Sign In</Link>
          </Button>
          <Button asChild>
            <a href="mailto:sales@topmatchtalent.com?subject=Request Demo">Request Demo</a>
          </Button>
        </div>

        {/* Mobile Menu - Using Sheet */}
        <div className="md:hidden flex flex-1 justify-end">
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:max-w-sm">
              <SheetHeader className="border-b pb-4 mb-4">
                <SheetTitle>
                   <Link to="/" onClick={handleLogoClick} className="inline-block">
                    <img
                      src="/images/logo.png" 
                      alt="TopMatchTalent Logo"
                      className="h-7 w-auto" 
                    />
                   </Link>
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col space-y-2">
                <SheetClose asChild>
                  <a 
                    href="#features" 
                    onClick={(e) => handleNavClick(e, '#features')} 
                    className={cn(navigationMenuTriggerStyle(), "justify-start text-base")}
                  >Features</a>
                </SheetClose>
                <SheetClose asChild>
                  <a 
                    href="#pricing" 
                    onClick={(e) => handleNavClick(e, '#pricing')}
                    className={cn(navigationMenuTriggerStyle(), "justify-start text-base")}
                  >Pricing</a>
                </SheetClose>
                <SheetClose asChild>
                  <a 
                    href="#how-it-works" 
                    onClick={(e) => handleNavClick(e, '#how-it-works')}
                    className={cn(navigationMenuTriggerStyle(), "justify-start text-base")}
                  >How it Works</a>
                </SheetClose>
                
                {/* Mobile Auth Buttons */}
                <div className="border-t pt-4 mt-4 space-y-2">
                    <SheetClose asChild>
                        {/* Fix: Don't use Button with asChild here */}
                        <a 
                          href="#contact" 
                          onClick={(e) => handleNavClick(e, '#contact')}
                          className="flex w-full items-center justify-start gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                          <Mail className="h-4 w-4" />
                          Contact Us
                        </a>
                    </SheetClose>
                    <SheetClose asChild>
                        <Button variant="outline" className="w-full justify-start" asChild>
                            <Link to="/login">Sign In</Link>
                        </Button>
                     </SheetClose>
                     <SheetClose asChild>
                        <Button className="w-full justify-start" asChild>
                             <a href="mailto:sales@topmatchtalent.com?subject=Request Demo">Request Demo</a>
                        </Button>
                     </SheetClose>
                 </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}